/**
 * The guides, in English — written for someone paid in the US.
 *
 * Every figure in an example is worked out in the text around it, so a reader
 * can redo the arithmetic. Anything
 * that depends on a law or a bank is stated as it generally is ("most
 * issuers"), never as a promise about the reader's own card.
 */
import { GuideLink } from './GuideLink';
import type { Guide, GuideId } from './types';

const PUBLISHED = '2026-10-19';
const UPDATED = '2026-11-03';

const budgetByPaycheck: Guide = {
  id: 'budget-by-paycheck',
  title: 'How to budget by paycheck',
  intro:
    'A monthly budget assumes the money arrives once. If you are paid on the 15th and the last day, or every other Friday, plan each paycheck instead: what it has to cover before the next one, and what is left to spend per day.',
  summary:
    'Plan each paycheck instead of the month: what it has to cover before the next one, what to set aside, and what is left to spend per day.',
  published: PUBLISHED,
  updated: UPDATED,
  sections: [
    {
      id: 'why',
      title: 'Why a monthly budget fits badly',
      blocks: [
        {
          kind: 'p',
          content:
            'A monthly budget adds up the month’s income and the month’s bills, and divides. The trouble is timing. Rent leaves on the 1st, out of the paycheck that arrived the day before; the card payment may fall in the other half of the month. One half carries most of the bills and the other almost none, and an average of the two describes neither.',
        },
        {
          kind: 'p',
          content:
            'The result is familiar: generous at the start of the month, tight at the end, and a budget that says you are fine right up until the week you are not.',
        },
      ],
    },
    {
      id: 'schedules',
      title: 'Semi-monthly, biweekly or weekly',
      blocks: [
        {
          kind: 'table',
          head: ['Paid', 'When', 'A year', 'What to watch'],
          rows: [
            [
              'Semi-monthly',
              'The 15th and the last day of the month',
              '24',
              'The two halves are not the same length: 13 to 16 days. A payday on a weekend is often paid the Friday before.',
            ],
            [
              'Biweekly',
              'Every other Friday, or another fixed weekday',
              '26',
              'Most years, two months have three paychecks — and which paycheck covers rent changes from month to month.',
            ],
            [
              'Weekly',
              'Every week',
              '52',
              'Four or five paychecks a month, so a monthly bill is a big share of whichever one it falls on.',
            ],
          ],
        },
        {
          kind: 'p',
          content:
            'The method below works for all three. A cycle runs from one payday to the day before the next.',
        },
      ],
    },
    {
      id: 'steps',
      title: 'Six steps for each paycheck',
      blocks: [
        { kind: 'h3', content: '1. Start from what lands in your account' },
        {
          kind: 'p',
          content:
            'Take-home pay — after taxes, insurance and retirement contributions — not the salary in your offer letter. If it varies, use the smallest of your last few paychecks.',
        },
        { kind: 'h3', content: '2. List what has to be paid before the next payday' },
        {
          kind: 'p',
          content:
            'Go through the calendar from today to the day before your next paycheck and write down every bill that falls in that window: rent or mortgage, utilities, phone and internet, insurance, loan and car payments, subscriptions, and the payment on any credit card whose due date falls inside it.',
        },
        {
          kind: 'p',
          content:
            'Give each bill to the paycheck that is in your account when it is due, not to the month it belongs to. Rent due on the 1st is paid from the paycheck before it. A bill that comes once a year — car insurance, a membership — is easier as a small amount set aside from every paycheck: divide it by 24 if you are paid twice a month, by 26 if every other week.',
        },
        { kind: 'h3', content: '3. Decide how much to pay on each card' },
        {
          kind: 'p',
          content: (
            <>
              The statement balance, if you can: it is the amount that avoids interest. The minimum
              keeps the account in good standing but charges interest on the rest. The difference
              between the two is in{' '}
              <GuideLink to="statement-and-due-dates">
                Statement closing date vs. due date
              </GuideLink>
              .
            </>
          ),
        },
        { kind: 'h3', content: '4. Set savings aside first' },
        {
          kind: 'p',
          content:
            'Move whatever you mean to save on payday, before anything else is spent — even a small fixed amount. What is left at the end of a cycle is rarely saved; what leaves on the first day usually is.',
        },
        { kind: 'h3', content: '5. Divide what is left by the days until the next payday' },
        {
          kind: 'p',
          content:
            'That figure is what you can spend per day on everything you decide as you go: groceries, gas, transit, eating out. It is the number worth checking.',
        },
        { kind: 'h3', content: '6. Check it halfway' },
        {
          kind: 'p',
          content:
            'Halfway through, divide what is actually left by the days actually left. If it is lower than you planned, the rest of the cycle adjusts by a little each day instead of all at once on the last two.',
        },
      ],
    },
    {
      id: 'example',
      title: 'An example',
      blocks: [
        {
          kind: 'p',
          content:
            'Take-home pay of $2,000 every other Friday, so fourteen days between paydays. The bills due before the next one:',
        },
        {
          kind: 'table',
          head: ['This paycheck', 'Amount'],
          rows: [
            ['Take-home pay', '$2,000'],
            ['Credit card payment (statement balance)', '−$500'],
            ['Phone and internet', '−$150'],
            ['Subscriptions', '−$50'],
            ['Savings, moved on payday', '−$250'],
            ['Left for everyday spending', '$1,050'],
            ['Per day, for 14 days', '$75'],
          ],
        },
        {
          kind: 'p',
          content:
            'The next paycheck carries rent, $1,050. With the same $250 saved it leaves $700 — $50 a day. Neither cycle is a bad one; one of them carries rent, and knowing that before it starts is the whole point of planning by paycheck.',
        },
        {
          kind: 'callout',
          content:
            'If the swing is too wide, set part of the rent aside from the lighter paycheck. Moving $175 from this one leaves both with $875: $62.50 a day, every cycle.',
        },
      ],
    },
    {
      id: 'extra-pay',
      title: 'Months with a third paycheck',
      blocks: [
        {
          kind: 'p',
          content:
            'Paid every two weeks, you get 26 paychecks a year, not 24, and most years two months have three paydays. Budget every month as if it had two, and the third arrives with nothing already assigned to it — the easiest money of the year to save, or to put towards a balance.',
        },
      ],
    },
    {
      id: 'irregular',
      title: 'If your income varies',
      blocks: [
        {
          kind: 'p',
          content:
            'Freelance, commission, tips: plan on what has arrived, not on what is expected. Cover the bills of the coming cycle first and treat anything above that as the next cycle’s money. A floor — the least you have earned in a recent month — makes a plan you can keep; an average makes an optimistic one.',
        },
        {
          kind: 'p',
          content:
            'If you are paid before tax, set the tax share aside the day the money arrives. It was never yours to spend, and a quarterly estimated payment is much easier from money already put away.',
        },
      ],
    },
    {
      id: 'twocoins',
      title: 'Doing it in TwoCoins',
      blocks: [
        {
          kind: 'p',
          content:
            'TwoCoins plans by the cycle you are paid on — half-month, every two weeks, weekly, monthly or a range you choose. It starts from what is in your accounts, takes off what your cards owe and each bill landing before your next payday, adds the pay still coming, and divides what is left by the days: step five, worked from your real balance and kept up to date as you log what you spend. If a bill lands before the pay that covers it, it says so, with the date.',
        },
        {
          kind: 'p',
          content:
            'It never asks for your bank login: you type each expense, or pick a store you have used before.',
        },
      ],
    },
  ],
};

const statementAndDueDates: Guide = {
  id: 'statement-and-due-dates',
  title: 'Statement closing date vs. due date',
  intro:
    'Every credit card has two dates a month. The closing date decides which bill a purchase lands on; the due date decides when that bill has to be paid. Knowing both is the difference between paying no interest and paying it every month.',
  summary:
    'What a card’s closing date and due date mean, which amount avoids interest, and the best day to make a big purchase.',
  published: PUBLISHED,
  updated: UPDATED,
  sections: [
    {
      id: 'two-dates',
      title: 'The two dates',
      blocks: [
        {
          kind: 'dl',
          items: [
            {
              term: 'Statement closing date',
              description:
                'The last day of the billing period. What you buy up to that day goes on this statement; what you buy after it goes on the next one. The statement balance is fixed that day.',
            },
            {
              term: 'Payment due date',
              description:
                'The day the payment for that statement has to arrive. In the US the statement must be sent at least 21 days before it, and the due date falls on the same day every month.',
            },
          ],
        },
        {
          kind: 'p',
          content:
            'The days in between are the grace period. Pay the whole statement balance within it and the purchases on that statement cost no interest at all.',
        },
      ],
    },
    {
      id: 'which-amount',
      title: 'Which amount to pay',
      blocks: [
        {
          kind: 'table',
          head: ['Amount', 'What it is', 'If you pay it'],
          rows: [
            [
              'Statement balance',
              'Everything charged up to the closing date, less what you have already paid.',
              'No interest on purchases. This is the one to aim for.',
            ],
            [
              'Minimum payment',
              'A small part of the balance, set by the issuer, usually with that month’s interest and fees.',
              'The account stays current, but interest is charged on the rest — and on most cards, new purchases lose their grace period until the balance is paid in full.',
            ],
            [
              'Current balance',
              'Everything up to today, including what you bought after the closing date.',
              'More than you need to avoid interest: the newer purchases are not due yet.',
            ],
          ],
        },
        {
          kind: 'p',
          content:
            'What the minimum costs is easy to work out. At 24% a year, interest is about 2% a month, so a $1,000 balance left unpaid costs about $20 the next month — and the month after, if it is still there.',
        },
        {
          kind: 'callout',
          content:
            'The minimum is not a suggestion of how much to pay. It is the least the issuer accepts before it counts the payment as late.',
        },
      ],
    },
    {
      id: 'best-day',
      title: 'The best day to make a big purchase',
      blocks: [
        {
          kind: 'p',
          content:
            'The day after your closing date. A purchase then lands on the next statement, which closes about a month later and is due some three weeks after that. Take a card that closes on the 12th and is due on the 6th of the next month:',
        },
        {
          kind: 'table',
          head: ['Bought on', 'Statement closing', 'Due', 'Days to pay'],
          rows: [
            ['September 11', 'September 12', 'October 6', '25'],
            ['September 13', 'October 12', 'November 6', '54'],
          ],
        },
        {
          kind: 'p',
          content:
            'Two days apart, a month’s difference. Purchases count by the day they post, which can be a day or two after you buy — so the day after the closing date is safe, and the closing date itself is not.',
        },
        {
          kind: 'p',
          content:
            'This is time, not free money: it only works if you pay that statement in full when it comes.',
        },
      ],
    },
    {
      id: 'credit-score',
      title: 'Your credit score and the closing date',
      blocks: [
        {
          kind: 'p',
          content:
            'Most issuers report the statement balance to the credit bureaus, so the utilisation your score sees is the balance on the closing date — even if you pay it in full every month. Before applying for a loan, paying part of the balance before the closing date lowers what is reported.',
        },
        {
          kind: 'p',
          content:
            'Late payments weigh far more. A payment a few days late usually costs a late fee; one more than 30 days late can be reported to the bureaus and stay on your credit report for up to seven years.',
        },
      ],
    },
    {
      id: 'several-cards',
      title: 'With more than one card',
      blocks: [
        {
          kind: 'p',
          content:
            'Each card has its own dates, so on most days one of them has just closed. That card gives you the longest time to pay for something bought today.',
        },
        {
          kind: 'p',
          content:
            'Many issuers let you choose your due date. One a few days after a payday means the money is there when the payment leaves.',
        },
      ],
    },
    {
      id: 'twocoins',
      title: 'Doing it in TwoCoins',
      blocks: [
        {
          kind: 'p',
          content:
            'Add each card’s closing and due days once. TwoCoins marks the best card for today — the one you pay for latest with no interest — counts what each card owes against what you have, shows each payment on its due date beside the paycheck that covers it, and keeps your limit beside your balance, so you see utilisation before the closing date reports it.',
        },
      ],
    },
  ],
};

const interestFreeInstalments: Guide = {
  id: 'interest-free-instalments',
  title: 'Interest-free instalments: how they really work',
  intro:
    'An interest-free instalment plan splits a purchase into equal payments at no extra cost — as long as every payment is made on time. The catch is rarely the interest. It is how much of your credit limit the plan holds, and how many plans you are paying at once.',
  summary:
    'How instalment plans use your credit limit, what stacking them does to each paycheck, and how deferred interest differs from 0%.',
  published: PUBLISHED,
  updated: UPDATED,
  sections: [
    {
      id: 'how',
      title: 'How an instalment plan works',
      blocks: [
        {
          kind: 'p',
          content:
            'The price is divided into equal monthly payments, and each statement includes one. If each one is paid in full and on time, the price never grows.',
        },
        {
          kind: 'p',
          content:
            'What surprises people is the credit limit. On a card, the whole price is held against your limit the day you buy, and released one payment at a time: a $1,200 phone on 12 payments uses $1,200 of your limit today, and $100 less each month after.',
        },
        {
          kind: 'p',
          content:
            'In Mexico this is meses sin intereses, offered by banks and stores on almost anything. In the US the nearest things are a card issuer’s instalment plan, a 0% promotion, and buy-now-pay-later services.',
        },
      ],
    },
    {
      id: 'stacking',
      title: 'Where plans go wrong: stacking',
      blocks: [
        {
          kind: 'table',
          head: ['Purchase', 'Price', 'Months', 'Each month'],
          rows: [
            ['Television', '$1,200', '12', '$100'],
            ['Phone', '$900', '18', '$50'],
            ['Flights', '$600', '6', '$100'],
            ['Laptop', '$1,500', '12', '$125'],
            ['All four, each month', '', '', '$375'],
          ],
        },
        {
          kind: 'p',
          content:
            'None of these is large on its own. Together they take $375 from every statement for six months, $275 for the six after that, and $50 until the phone is paid off — before anything else is bought. Each new plan is judged by its own payment; the total is what the next year actually feels like.',
        },
      ],
    },
    {
      id: 'missed',
      title: 'If a payment is missed',
      blocks: [
        {
          kind: 'p',
          content:
            'On a card, the month’s instalment is part of what is due. Pay less than that and interest is charged on what is left — and, depending on the issuer, the plan can lose its 0%. The terms say which; read them before you agree, not after.',
        },
      ],
    },
    {
      id: 'deferred-interest',
      title: 'Deferred interest is not 0%',
      blocks: [
        {
          kind: 'p',
          content:
            'Store cards and medical financing often advertise “no interest if paid in full in 12 months”. That is deferred interest: interest builds up from the day you buy, and if any of the balance is left when the period ends, all of it is charged at once — on the original price, back to the first day.',
        },
        {
          kind: 'table',
          head: ['', 'Deferred interest', '0% APR'],
          rows: [
            ['Paid in full in time', 'No interest', 'No interest'],
            [
              '$50 left at the end',
              'Interest on the whole original purchase, from the day you bought it',
              'Interest on the $50, from then on',
            ],
            ['How it is worded', '“No interest if paid in full”', '“0% intro APR”'],
          ],
        },
        {
          kind: 'p',
          content:
            'Some issuers’ instalment plans charge a fixed monthly fee instead of interest. The fee is the cost, and it is shown before you accept.',
        },
      ],
    },
    {
      id: 'pay-in-four',
      title: 'Buy now, pay later',
      blocks: [
        {
          kind: 'p',
          content:
            'Pay-in-four services split a purchase into four payments over six weeks, usually with no interest. They are not on your card statement, which is what makes them easy to lose track of: each one is still a date money leaves your account. Some charge late fees, and their longer plans can charge interest.',
        },
      ],
    },
    {
      id: 'worth-it',
      title: 'When a plan is worth it',
      blocks: [
        {
          kind: 'ul',
          items: [
            'The price on the plan is the same as paying at once. If paying at once is cheaper, the plan has a cost — compare them.',
            'You would buy it anyway, and spreading it keeps cash for the rest of the cycle.',
            'The total of all your plans — not just this one — still leaves room in every paycheck.',
            'You know the month it ends.',
          ],
        },
      ],
    },
    {
      id: 'twocoins',
      title: 'Doing it in TwoCoins',
      blocks: [
        {
          kind: 'p',
          content:
            'Any payment that repeats goes in once, as a commitment with the day it posts, and comes out of what is free in the cycle it lands in — never the whole price in the month you bought. Loans and plans with interest also take the number of payments, and show the month they end. In the Mexico market, meses sin intereses get their own kind of plan.',
        },
      ],
    },
  ],
};

export const guidesEn: Record<GuideId, Guide> = {
  'budget-by-paycheck': budgetByPaycheck,
  'statement-and-due-dates': statementAndDueDates,
  'interest-free-instalments': interestFreeInstalments,
};
